import {
    CheckIcon,
    ClockIcon,
    EnvelopeIcon,
    PhoneIcon,
} from '@heroicons/react/20/solid';

import { Button, EButtonType } from '@/components/Button';
import { ContactPanel } from '@/components/ContactPanel';
import { DisplayComponent } from '@/components/DisplayComponent';
import { Header } from '@/components/Header';
import { OutcomeCard } from '@/components/OutcomeCard';
import { RemoteImage } from '@/components/RemoteImage';
import {
    EDisplays,
    selectPartnerEmail,
    selectPartnerNumber,
    setDisplay,
} from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { EMAIL, PHONE } from '@/utils/constants';

export const testId = 'screen-IndicativelyReferred';

export function IndicativelyReferred(): JSX.Element {
    const baseId = 'screen-IndicativelyReferred';
    const { outcomes, referredTimeline, referredDetail } = useCopy();
    const outcome = outcomes.referred;
    const dispatch = useAppDispatch();
    const phoneNumber = useAppSelector(selectPartnerNumber) || PHONE;

    function handleBackToStart(): void {
        dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
    }
    const email = useAppSelector(selectPartnerEmail) || EMAIL;

    if (referredTimeline) {
        return (
            <div data-testid={testId}>
                <DisplayComponent baseId={baseId}>
                    <Header />
                    <OutcomeCard
                        icon={<ClockIcon className='h-6 w-6' />}
                        tint='bg-referTint text-primary'
                        title={outcome.title}
                        lines={[referredDetail.intro, referredDetail.body]}
                    >
                        <ol className='_flex flex-col pt-1'>
                            {referredDetail.steps.map((step, index) => {
                                const done = index === 0;
                                const current = index === 1;
                                const last =
                                    index === referredDetail.steps.length - 1;
                                return (
                                    <li
                                        key={step.title}
                                        className='_flex gap-3'
                                    >
                                        <div className='_flex flex-col items-center'>
                                            {done ? (
                                                <div className='h-5 w-5 shrink-0 rounded-full bg-success text-white flex-center'>
                                                    <CheckIcon className='h-3 w-3' />
                                                </div>
                                            ) : (
                                                <div
                                                    className={`h-5 w-5 shrink-0 rounded-full border-2 ${
                                                        current
                                                            ? 'border-primary'
                                                            : 'border-referRing'
                                                    }`}
                                                />
                                            )}
                                            {!last && (
                                                <div className='w-px flex-1 bg-referRing' />
                                            )}
                                        </div>
                                        <div
                                            className={last ? 'pb-0' : 'pb-3.5'}
                                        >
                                            <p
                                                className={`text-sm font-bold ${
                                                    done || current
                                                        ? 'text-panel'
                                                        : 'text-textBody'
                                                }`}
                                            >
                                                {step.title}
                                            </p>
                                            <p className='text-[13px] text-textBody'>
                                                {step.detail}
                                            </p>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    </OutcomeCard>

                    {/* The number is partner config, not copy — the wording
                        around it is all that lives in the copy layer. */}
                    <ContactPanel
                        baseId={baseId}
                        text={referredDetail.contactText}
                        cta={referredDetail.callCta}
                        phone={phoneNumber}
                    />
                    {/* These screens end the journey. Without this the only
                        way out is closing the widget. */}
                    <div className='_flex pt-3 pb-2'>
                        <Button
                            id={`${baseId}-button-backToStart`}
                            onClick={handleBackToStart}
                            type={EButtonType.SECONDARY_OUTLINE}
                        >
                            <p className='text-sm'>{referredDetail.backToStart}</p>
                        </Button>
                    </div>
                </DisplayComponent>
            </div>
        );
    }

    return (
        <div data-testid={testId}>
            <DisplayComponent baseId={baseId}>
                <div className='px-8'>
                    <Header />
                </div>
                <div className='flex-center pt-20 pb-10 relative'>
                    <div className='bg-primary h-40 w-40 rounded-full' />
                    <RemoteImage fileName='humans.svg' className='absolute' />
                </div>
                <div className='text-center _flex flex-col space-y-2'>
                    <h3 className='uppercase text-xl text-primary font-bold '>
                        {outcome.title}
                    </h3>
                    {outcome.body.map((line) => (
                        <p
                            key={line}
                            className='text-secondary whitespace-pre-line'
                        >
                            {line}
                        </p>
                    ))}
                </div>

                <div className='flex-center w-full pt-4'>
                    <div className='border-lg border-2 text-sm bg-white py-2 rounded-xl px-10 text-gray-500 text-center'>
                        <h3 className='font-bold pb-4'>
                            Have a question? <br />
                            Get in touch:
                        </h3>
                        <a
                            href={`tel:${phoneNumber}`}
                            className='flex-center space-x-2'
                        >
                            <PhoneIcon className='h-4 w-4' />
                            <p>{phoneNumber}</p>
                        </a>
                        <a
                            href={`mailto:${email}`}
                            className='flex-center space-x-2'
                        >
                            <EnvelopeIcon className='h-4 w-4' />
                            <p>{email}</p>
                        </a>
                    </div>
                    {/* <Button onClick={handleClick} /> */}
                </div>
            </DisplayComponent>
        </div>
    );
}
